import React from 'react';
import Icon from '../../../AppIcon';
import Button from '../../../ui/Button';
import Image from '../../../AppImage';

const ProgramCard = ({ program, onViewDetails, onApply, userRole = 'startup' }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success bg-success/10';
      case 'closing-soon': return 'text-warning bg-warning/10';
      case 'closed': return 'text-muted-foreground bg-muted';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getMatchScore = (score) => {
    if (score >= 90) return { color: 'text-success', bg: 'bg-success/10', label: 'Excellent Match' };
    if (score >= 75) return { color: 'text-primary', bg: 'bg-primary/10', label: 'Good Match' };
    if (score >= 60) return { color: 'text-warning', bg: 'bg-warning/10', label: 'Fair Match' };
    return { color: 'text-muted-foreground', bg: 'bg-muted', label: 'Low Match' };
  };

  const matchInfo = getMatchScore(program?.matchScore);

  return (
    <div className="bg-card border border-border rounded-lg p-6 hover:shadow-lg transition-all duration-200 hover:-translate-y-1">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted flex-shrink-0">
            <Image
              src={program?.companyLogo}
              alt={program?.companyLogoAlt}
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-1">{program?.title}</h3>
            <p className="text-sm text-muted-foreground">{program?.company}</p>
          </div>
        </div>
        
        <div className="flex flex-col items-end space-y-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(program?.status)}`}>
            {program?.status?.replace('-', ' ')?.toUpperCase()}
          </span>
          {program?.matchScore && (
            <div className={`px-2 py-1 rounded-full text-xs font-medium ${matchInfo?.color} ${matchInfo?.bg}`}>
              {program?.matchScore}% Match
            </div>
          )}
        </div>
      </div>
      {/* Description */}
      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
        {program?.description}
      </p>
      {/* Key Details */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="flex items-center space-x-2">
          <Icon name="MapPin" size={14} className="text-muted-foreground" />
          <span className="text-sm text-foreground">{program?.location}</span>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Calendar" size={14} className="text-muted-foreground" />
          <span className="text-sm text-foreground">{program?.duration}</span>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="DollarSign" size={14} className="text-muted-foreground" />
          <span className="text-sm text-foreground">{program?.funding}</span>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Users" size={14} className="text-muted-foreground" />
          <span className="text-sm text-foreground">{program?.participants} startups</span>
        </div>
      </div>
      {/* Tags */}
      <div className="flex flex-wrap gap-2 mb-4">
        {program?.tags?.slice(0, 3)?.map((tag, index) => (
          <span
            key={index}
            className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-md"
          >
            {tag}
          </span>
        ))}
        {program?.tags?.length > 3 && (
          <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-md">
            +{program?.tags?.length - 3} more
          </span>
        )}
      </div>
      {/* Application Deadline */}
      {program?.deadline && (
        <div className="flex items-center space-x-2 mb-4 p-2 bg-warning/10 rounded-md">
          <Icon name="Clock" size={14} className="text-warning" />
          <span className="text-sm text-warning font-medium">
            Deadline: {program?.deadline}
          </span>
        </div>
      )}
      {/* Match Reasons (if available) */}
      {program?.matchReasons && program?.matchReasons?.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-medium text-muted-foreground mb-2">Why this matches:</p>
          <div className="space-y-1">
            {program?.matchReasons?.slice(0, 2)?.map((reason, index) => (
              <div key={index} className="flex items-center space-x-2">
                <Icon name="Check" size={12} className="text-success" />
                <span className="text-xs text-foreground">{reason}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-1">
            <Icon name="Eye" size={14} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">{program?.views} views</span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="Users" size={14} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">{program?.applications} applied</span>
          </div>
          {program?.verified && (
            <div className="flex items-center space-x-1">
              <Icon name="Shield" size={14} className="text-success" />
              <span className="text-xs text-success">Verified</span>
            </div>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewDetails(program)}
            iconName="Eye"
            iconPosition="left"
            iconSize={14}
          >
            Details
          </Button>
          {userRole === 'startup' && program?.status === 'active' && (
            <Button
              variant="default"
              size="sm"
              onClick={() => onApply(program)}
              iconName="Send"
              iconPosition="left"
              iconSize={14}
            >
              Apply
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProgramCard;